import { useBoolean } from 'usehooks-ts';
import { useTranslation } from 'react-i18next';
import { useEffect } from 'react';

import { log } from '@app/shared/utils/logger';
import { ScrollArea } from '@app/shared/ui/atoms/Common';
import {
  TopSection,
  FeedPandaSection,
  AllStones,
  SettlementsSection,
} from '@app/shared/ui/organisms/Home';
import { Loading, Modal, ProfitCalculator } from '@app/shared/ui/organisms/Common';
import {
  useGetCurrentUserProfileQuery,
  useGetCurrentUserQuery,
  useGetTotalUsersQuery,
  usersApi,
  useUpdateCurrentUserProfileMutation,
} from '@app/store/slices/users/usersApi';
import { useGetCurrentUserProgressQuery } from '@app/store/slices/userProgress/userProgressApi';
import { useGetStonesQuery } from '@app/store/slices/stones/stonesApi';
import { usePreloadImages } from '@app/shared/hooks/usePreloadImages';
import { useGetSettlementsQuery } from '@app/store/slices/settlements/settlementsApi';
import { telegramShareInviteMessageWithInitialReward } from '@app/shared/utils/common';
import { HOME_PRELOAD_IMAGES } from '@app/assets';
import { useDisplayError } from '@app/shared/hooks/useDisplayError';
import { NotFound } from '@app/shared/ui/molecules/Common';
import notification from '@app/shared/utils/notification';
import { useAppDispatch, useAppSelector } from '@app/store/hooks';
import { tutorialActions } from '@app/store/slices/tutorial/tutorialSlice';
import { useMinDelay } from '@app/shared/hooks/useMinDelay';

export default function HomePage() {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const tutorial = useAppSelector((state) => state.tutorial);

  const {
    data: usersTotal,
    isLoading: isUsersTotalLoading,
    isError: isUsersTotalError,
  } = useGetTotalUsersQuery();
  const {
    value: isProfitCalculatorModalOpen,
    setValue: setIsProfitCalculatorModalOpen,
    setTrue: openProfitCalculatorModal,
    setFalse: closeProfitCalculatorModal,
  } = useBoolean(false);

  const { data: user, isLoading: isUserLoading, isError: isUserError } = useGetCurrentUserQuery();
  const {
    data: userProfile,
    isLoading: isUserProfileLoading,
    isError: isUserProfileError,
  } = useGetCurrentUserProfileQuery();
  const [updateCurrentUserProfile] = useUpdateCurrentUserProfileMutation();
  const {
    data: userProgress,
    isLoading: isUserProgressLoading,
    isError: isUserProgressError,
  } = useGetCurrentUserProgressQuery();
  const {
    data: settlements,
    isLoading: isSettlementsLoading,
    isError: isSettlementsLoadingFail,
  } = useGetSettlementsQuery();

  const {
    data: stonesResponse,
    isLoading: isStonesLoading,
    isError: isStonesLoadingFail,
  } = useGetStonesQuery();
  const holdAfterTotal = useMinDelay(Boolean(usersTotal), 800);

  const stones = stonesResponse?.data ?? [];
  const isImagesLoading = usePreloadImages(HOME_PRELOAD_IMAGES);

  const failedToLoadStonesMsg = t('errors.failed_to_load_stones');
  const failedToLoadSettlementsMsg = t('errors.failed_to_load_settlements');

  useDisplayError(isStonesLoadingFail, failedToLoadStonesMsg);
  useDisplayError(isSettlementsLoadingFail, failedToLoadSettlementsMsg);

  const baseReady =
    !isUserLoading &&
    !isStonesLoading &&
    !isUserProgressLoading &&
    !isSettlementsLoading &&
    !isImagesLoading;

  const isPageReady = baseReady && holdAfterTotal;

  useEffect(() => {
    void log({
      level: 'info',
      message: 'authHomeDataState',
      extra: {
        isPageReady,
        baseReady,
        holdAfterTotal,
        queries: {
          usersTotal: {
            isLoading: isUsersTotalLoading,
            isError: isUsersTotalError,
            hasData: !!usersTotal,
          },
          user: {
            isLoading: isUserLoading,
            isError: isUserError,
            hasData: !!user,
          },
          userProfile: {
            isLoading: isUserProfileLoading,
            isError: isUserProfileError,
            hasData: !!userProfile,
          },
          userProgress: {
            isLoading: isUserProgressLoading,
            isError: isUserProgressError,
            hasData: !!userProgress,
          },
          settlements: {
            isLoading: isSettlementsLoading,
            isError: isSettlementsLoadingFail,
            hasData: !!settlements,
          },
          stones: {
            isLoading: isStonesLoading,
            isError: isStonesLoadingFail,
            hasData: !!stonesResponse,
          },
          images: {
            isLoading: isImagesLoading,
          },
        },
      },
    });
  }, [
    baseReady,
    holdAfterTotal,
    isImagesLoading,
    isPageReady,
    isSettlementsLoading,
    isSettlementsLoadingFail,
    isStonesLoading,
    isStonesLoadingFail,
    isUserError,
    isUserLoading,
    isUserProfileError,
    isUserProfileLoading,
    isUserProgressError,
    isUserProgressLoading,
    isUsersTotalError,
    isUsersTotalLoading,
    settlements,
    stonesResponse,
    user,
    userProfile,
    userProgress,
    usersTotal,
  ]);

  useEffect(() => {
    dispatch(tutorialActions.onHomeMounted());
    if (!tutorial.manual && userProfile?.isTutorialCompleted) {
      dispatch(tutorialActions.skip());
    }
    if (tutorial.step === 'CALC_1' || tutorial.step === 'CALC_2') {
      openProfitCalculatorModal();
    } else if (
      tutorial.step === 'HOME_STONE_1' ||
      tutorial.step === 'HOME_SETTLEMENT_1' ||
      tutorial.step === 'HOME_CALC_1'
    ) {
      closeProfitCalculatorModal();
    }
    void log({ level: 'warn', message: 'Tutorial state', extra: { tutorial, baseReady } });
  }, [tutorial, isUserProfileLoading, userProfile]);

  useEffect(() => {
    if (tutorial.step === 'DONE' && !tutorial.manual && tutorial.complete) {
      void updateCurrentUserProfile({
        isTutorialCompleted: true,
      }).unwrap();
      dispatch(
        usersApi.util.updateQueryData('getCurrentUserProfile', undefined, (draft) => {
          if (draft) {
            draft.isTutorialCompleted = true;
          }
        })
      );
    }
  }, [tutorial.step, tutorial.manual, tutorial.complete]);

  if (!isPageReady) {
    return <Loading />;
  }

  const handleQuestionClick = () => {
    notification.warning(t('common.soon'));
  };

  return (
    <ScrollArea className="xs:px-3 sm:px-5 pt-5">
      <TopSection
        userProgress={userProgress}
        onPlusBtnClick={() => {
          if (user) {
            telegramShareInviteMessageWithInitialReward(user, t);
          }
        }}
        settlements={settlements}
      />

      <FeedPandaSection lastPandaFed={userProgress?.lastPandaFedAt} containerClassName="mt-2" />

      <AllStones stones={stones} containerClassName="mt-3" />

      {settlements ? (
        <SettlementsSection
          containerClassName="mt-7"
          onCalculatorBtnClick={openProfitCalculatorModal}
          onQuestionBtnClick={handleQuestionClick}
          settlements={settlements}
        />
      ) : (
        <NotFound containerClassName="text-foreground" message={t('errors.general')} />
      )}

      <Modal
        isOpen={isProfitCalculatorModalOpen}
        onOpenChange={setIsProfitCalculatorModalOpen}
        contentClassName="pt-20 shadow-blue"
        hiddenTitle="Profit calculator"
      >
        <ProfitCalculator settlements={settlements} />
      </Modal>
    </ScrollArea>
  );
}
